import { createIsomorphicFn } from "@tanstack/react-start";
import { getCookie } from "@tanstack/react-start/server";
import * as cookie from "cookie";
import { z } from "zod";

const PREFERENCES_COOKIE_NAME = "preferences";

const PREFERENCES_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

const preferencesSchema = z.object({
  sidebarOpen: z.boolean().catch(true),
  calendarView: z.enum(["month", "week"]).catch("month"),
  programGridZoom: z.literal([1, 2, 3, 4]).catch(4),
  workoutDialogFullScreen: z.boolean().nullable().catch(null),
  workoutDialogSidebarOpen: z.boolean().catch(true),
});

const savedPreferencesSchema = preferencesSchema.partial();

export type Preferences = z.infer<typeof preferencesSchema>;

const listeners = new Set<() => void>();

function parseSavedPreferences(
  value: string | undefined,
): Partial<Preferences> {
  try {
    return savedPreferencesSchema.parse(JSON.parse(value ?? "{}"));
  } catch {
    return {};
  }
}

const getSavedPreferences: () => Partial<Preferences> = createIsomorphicFn()
  .server(() => {
    return parseSavedPreferences(getCookie(PREFERENCES_COOKIE_NAME));
  })
  .client(() => {
    return parseSavedPreferences(
      cookie.parse(document.cookie)[PREFERENCES_COOKIE_NAME],
    );
  });

export function getPreferences(): Preferences {
  return preferencesSchema.parse(getSavedPreferences());
}

export function setPreference<TKey extends keyof Preferences>(
  key: TKey,
  value: Preferences[TKey],
) {
  document.cookie = cookie.serialize(
    PREFERENCES_COOKIE_NAME,
    JSON.stringify({ ...getSavedPreferences(), [key]: value }),
    {
      path: "/",
      maxAge: PREFERENCES_COOKIE_MAX_AGE,
      sameSite: "lax",
    },
  );
  listeners.forEach((listener) => {
    listener();
  });
}

export function subscribeToPreferences(listener: () => void) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}
